import { useState, useEffect, useMemo } from 'react';
import { Box, TextField, Autocomplete, Grid, Typography } from '../mui';
import { LocationOnIcon } from '../icons';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { funcType } from 'containers/pages/contacts/types';

const autocompleteService = { current: null };

const PlaceAutoComplete = ({ handlePlace }) => {
	const [inputValue, setInputValue] = useState('');
	const [options, setOptions] = useState([]);
	const [address, setAddress] = useState(null);

	const fetch = useMemo(
		() =>
			throttle((request, callback) => {
				autocompleteService.current.getPlacePredictions(
					request,
					callback,
				);
			}, 200),
		[],
	);

	useEffect(() => {
		let active = true;

		if (!autocompleteService.current && window.google) {
			autocompleteService.current =
				new window.google.maps.places.AutocompleteService();
		}

		if (!autocompleteService.current) return undefined;

		if (inputValue === '') {
			setOptions(address ? [address] : []);
			return undefined;
		}

		if (inputValue.length > 2) {
			fetch(
				{
					input: inputValue,
					location: new window.google.maps.LatLng(52, -113),
					radius: 300,
					componentRestrictions: { country: ['ca', 'us'] },
					types: ['geocode'],
				},
				(results) => {
					if (active) {
						let newOptions = [];
						if (address) newOptions = [address];
						if (results) newOptions = [...newOptions, ...results];
						setOptions(newOptions);
					}
				},
			);
		}
		return () => (active = false);
	}, [address, inputValue, fetch]);

	useEffect(() => {
		const fetchPlace = async () => {
			try {
				const _geocodeByAddress = await geocodeByAddress(
					address.description,
				);
				const _getLatLng = await getLatLng(_geocodeByAddress[0]);
				const placeData = {
					geoCode: _geocodeByAddress,
					latLng: _getLatLng,
				};
				handlePlace(placeData);
				setAddress(null);
			} catch (error) {
				console.log('CATCH ERROR :>> ', error);
			}
		};
		if (address) {
			fetchPlace();
		}
	}, [address, setAddress, handlePlace]);

	return (
		<Autocomplete
			getOptionLabel={(option) =>
				typeof option === 'string' ? option : option.description
			}
			filterOptions={(x) => x}
			options={options}
			autoComplete
			includeInputInList
			filterSelectedOptions
			value={address}
			noOptionsText="No address or not found"
			onChange={(event, newValue) => {
				setOptions(newValue ? [newValue, ...options] : options);
				setAddress(newValue);
			}}
			onInputChange={(event, newInputValue) => {
				setInputValue(newInputValue);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Search Address"
					InputLabelProps={{ shrink: true }}
					fullWidth
				/>
			)}
			renderOption={(props, option) => {
				const matches =
					option.structured_formatting.main_text_matched_substrings;
				const parts = parse(
					option.structured_formatting.main_text,
					matches.map((match) => [
						match.offset,
						match.offset + match.length,
					]),
				);

				return (
					<li {...props}>
						<Grid container alignItems="center">
							<Grid item>
								<Box
									component={LocationOnIcon}
									sx={{ color: 'text.secondary', mr: 2 }}
								/>
							</Grid>
							<Grid item xs>
								{parts.map((part, index) => (
									<span
										key={index}
										style={{
											fontWeight: part.highlight
												? 700
												: 400,
										}}
									>
										{part.text}
									</span>
								))}
								<Typography
									variant="body2"
									color="text.secondary"
								>
									{
										option.structured_formatting
											.secondary_text
									}
								</Typography>
							</Grid>
						</Grid>
					</li>
				);
			}}
		/>
	);
};

PlaceAutoComplete.propTypes = {
	handlePlace: funcType,
};

export default PlaceAutoComplete;
